import React, { useEffect, useState } from 'react';

import { CheckOutlined } from '@ant-design/icons';
import { schemas } from '@recurrency/core-api-schema';
import { VendorItemDTO } from '@recurrency/core-api-schema/dist/vendors/getVendorItems';
import { Radio } from 'antd';
import { ColumnType } from 'antd/lib/table';
import { useDebounce } from 'use-debounce/lib';

import { AsyncTable } from 'components/AsyncTable';
import { useCoreApiTableProps } from 'components/AsyncTable/useAsyncTableProps';
import { FilterBarBox } from 'components/FilterBarBox';
import { FlexSpacer } from 'components/FlexSpacer';
import { ItemLink } from 'components/Links';
import { RadioGroup } from 'components/Radio';
import { ResultCount } from 'components/ResultCount';
import { SearchInput } from 'components/SearchInput';

import { formatDate } from 'utils/formatting';
import { sortableBooleanColumn, sortableDateColumn, sortableNumberColumn, asKeyOf, sortDirections } from 'utils/tables';

/**
 * Items across all suppliers of the vendor. The active company selection is applied by core-api
 * (`companyIdsFilterPath`), so no company is passed here.
 */
export const VendorItemsTable = ({ vendorId }: { vendorId: string }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery] = useDebounce(searchQuery, 500);
  // defaults to the items counted in the vendor header's stockable item count
  const [stockableOnly, setStockableOnly] = useState(true);

  const tableProps = useCoreApiTableProps({
    schema: schemas.vendors.getVendorItems,
    pathParams: { vendorId },
    queryParams: { query: debouncedSearchQuery.trim() || undefined, stockableOnly },
    initialState: { sortBy: 'itemCode', sortDir: 'asc' },
  });

  const { setPage } = tableProps;
  useEffect(() => {
    setPage(1);
  }, [debouncedSearchQuery, stockableOnly, setPage]);

  const columns: ColumnType<VendorItemDTO>[] = [
    {
      title: 'Item ID',
      dataIndex: asKeyOf<VendorItemDTO>('itemCode'),
      render: (itemCode: string) => <ItemLink itemCode={itemCode}>{itemCode}</ItemLink>,
      sorter: true,
      sortDirections,
      defaultSortOrder: 'ascend',
    },
    {
      title: 'Supplier Part ID',
      dataIndex: asKeyOf<VendorItemDTO>('supplierPartId'),
      render: (supplierPartId?: string) => supplierPartId ?? '-',
      sorter: true,
      sortDirections,
    },
    sortableDateColumn({
      title: 'Last Purchase Date',
      dataIndex: asKeyOf<VendorItemDTO>('lastPurchaseDate'),
      render: (date?: string) => (date ? formatDate(date) : '-'),
      sorter: true,
    }),
    sortableBooleanColumn({
      title: 'Stockable',
      dataIndex: asKeyOf<VendorItemDTO>('stockable'),
      render: (stockable: boolean) => (stockable ? <CheckOutlined /> : null),
      sorter: true,
    }),
    sortableNumberColumn({
      title: 'Net Stock',
      dataIndex: asKeyOf<VendorItemDTO>('netStock'),
      sorter: true,
    }),
  ];

  return (
    <>
      <FilterBarBox>
        <RadioGroup value={stockableOnly} onChange={({ target: { value } }) => setStockableOnly(value)}>
          <Radio.Button value>Stockable</Radio.Button>
          <Radio.Button value={false}>All</Radio.Button>
        </RadioGroup>
        <FlexSpacer />
        <SearchInput
          placeholder="Search item ID, supplier part ID, or description"
          query={searchQuery}
          onQueryChange={setSearchQuery}
        />
        <ResultCount count={tableProps.totalCount} />
      </FilterBarBox>
      <AsyncTable tableProps={tableProps} columns={columns} rowKey={asKeyOf<VendorItemDTO>('itemId')} />
    </>
  );
};
